import { CodeBlock, dracula } from 'react-code-blocks';
import { Fragment } from 'react';
import { StyleRecord } from '../../types';

/**
 * Splits a chunk of text on single backticks and renders the fenced parts as
 * inline `<code>`.
 */
function InlineText({ text, styles }: { text?: string; styles: StyleRecord }) {
    if (!text) return null;

    return <>
        {text.split(/(`.*?`)/g).map((part, index) => part.startsWith('`')
            ? <code key={index} className={styles.code}>{part.slice(1, -1)}</code>
            : <span key={index}>{part}</span>)}
    </>;
};

/**
 * `none`    – never render the snippet container.
 * `auto`    – render it only when the question actually carries a snippet.
 * `always`  – always render the container, snippet or not (keeps its margins).
 */
type CodeBlockMode = 'none' | 'auto' | 'always';

interface QuestionTextProps {
    styles: StyleRecord;
    question: string;
    /** Prefix rendered before the question, e.g. "1 - ". */
    prefix?: string;
    codeBlock?: CodeBlockMode;
};

/**
 * A question body is `text ``` code ``` text`. Both halves may additionally
 * contain single-backtick inline code.
 */
export function QuestionText(props: QuestionTextProps) {
    const { styles, question, prefix, codeBlock = 'none' } = props;
    const [before, snippet, after] = question.split('```');
    const hasSnippet = snippet !== undefined;

    return <>
        <span className={styles.question}>
            {prefix}
            <InlineText text={before} styles={styles} />
        </span>

        {codeBlock !== 'none' && (hasSnippet || codeBlock === 'always') &&
            <div className={styles.codeBlock}>
                {hasSnippet && <CodeBlock
                    text={snippet}
                    language='python'
                    showLineNumbers={true}
                    theme={dracula}
                />}
            </div>}

        <span className={styles.question}>
            <InlineText text={after} styles={styles} />
        </span>
    </>;
};

interface QuestionChoicesProps {
    styles: StyleRecord;
    choices: string[];
    /** Index of the correct choice. */
    answer: number;
    /** Index picked by the applicant, when the exam has been taken. */
    examinerAnswer?: number | string;
};

export function QuestionChoices(props: QuestionChoicesProps) {
    const { styles, choices, answer, examinerAnswer } = props;
    const picked = examinerAnswer === undefined ? undefined : `${examinerAnswer}`;

    return (
        <div className={styles.options}>
            {choices.map((choice, index) => <div key={index} className={styles.option}>
                <span
                    className={styles.option_index}
                    data-correct-answer={answer === index}
                    data-wrong-examiner-answer={picked !== undefined && picked === `${index}` && picked !== `${answer}`}
                >
                    {index + 1}
                </span> - &nbsp;
                {choice.includes('```')
                    ? <div className={styles.code_block}>
                        <CodeBlock
                            text={choice.split('```')[1]}
                            language='python'
                            showLineNumbers={true}
                            theme={dracula}
                        />
                    </div>
                    : <Fragment><InlineText text={choice} styles={styles} /></Fragment>}
            </div>)}
        </div>
    );
};
